<template>
    <img ref="characterImage" :src="img_src" alt="character" />    
</template>

<script>
import { mapState } from 'vuex'
import { FALLBACK_RENDER_JOB_IDS, FALLBACK_RENDER_VIEW_IDS } from '@/config/jobs'

// Endpoints that just failed, and when they may be tried first again.
// A backend outage (the primary has 502'd for hours at a time) otherwise costs
// every single render a doomed round-trip before the failover kicks in. After a
// failure we push that endpoint to the back of the queue for a minute, so the
// healthy backend answers immediately; once the minute lapses it gets promoted
// again, which is how we notice it recovered without polling it.
const ENDPOINT_COOLDOWN_MS = 60000;
const endpointFailedUntil = new Map();

export default {
    name: "Character",
    data() {
        return {
            img_src: ""
        }
    },
    created() {
        // Plumbing, deliberately not reactive: rendered sprites keyed by the exact
        // character body, plus in-flight requests so duplicates collapse.
        this.RENDER_CACHE_LIMIT = 60;
        this._renderCache = new Map();
        this._inFlight = new Map();
        this._lastBody = null;
    },
    methods: {
        // Ordered list of render backends to try: [preferred, failover].
        // The preferred backend is chosen the same way as before (iRO Wiki for
        // jobs/items/hair the primary can't render, primary otherwise); the
        // other backend is the automatic failover if the first one is down.
        resolveRenderEndpoints: function () {
            const jobId = parseInt(this.character.job?.[0], 10);
            // Prefer the fallback backend if the job OR any equipped item is
            // one the primary backend can't render.
            const equippedViewIds = [
                ...(this.character.headgear || []),
                this.character.garment,
            ];
            // Human hairstyles above 31 only exist on the iRO Wiki backend
            // (the primary caps at 31). Doram hair never exceeds 10, so this
            // only triggers for the newer human styles 32-42.
            const head = parseInt(this.character.head, 10);
            const needsFallback =
                FALLBACK_RENDER_JOB_IDS.has(jobId) ||
                head > 31 ||
                equippedViewIds.some((v) => FALLBACK_RENDER_VIEW_IDS.has(parseInt(v, 10)));

            const primary =
                process.env.VUE_APP_API_URL +
                "&accesstoken=" +
                process.env.VUE_APP_API_KEY;
            const iro =
                process.env.VUE_APP_FALLBACK_API_URL &&
                process.env.VUE_APP_FALLBACK_API_KEY
                    ? process.env.VUE_APP_FALLBACK_API_URL +
                      "&accesstoken=" +
                      process.env.VUE_APP_FALLBACK_API_KEY
                    : null;

            // Without a configured fallback, primary is the only option.
            if (!iro) return [primary];
            // Preferred backend first; the other one is the automatic failover.
            return needsFallback ? [iro, primary] : [primary, iro];
        },
        // Fetch a sprite for `char`, trying the preferred backend then the
        // failover. Returns an object URL, or null if every endpoint failed.
        fetchSprite: async function (char) {
            const now = Date.now();
            const all = this.resolveRenderEndpoints();
            // Endpoints known to be failing go last, but are never dropped: if
            // every backend is cooling we still try them all rather than give up.
            const healthy = all.filter((u) => (endpointFailedUntil.get(u) || 0) <= now);
            const cooling = all.filter((u) => (endpointFailedUntil.get(u) || 0) > now);
            const endpoints = [...healthy, ...cooling];

            for (let i = 0; i < endpoints.length; i++) {
                const isLast = i === endpoints.length - 1;
                const url = endpoints[i];
                try {
                    const response = await fetch(url, {
                        method: "POST",
                        headers: { 'Content-Type': 'application/vnd.api+json' },
                        body: char,
                    });
                    // Backend error (e.g. nginx 502 when the renderer is down) →
                    // fall over to the next endpoint.
                    if (!response.ok) { endpointFailedUntil.set(url, Date.now() + ENDPOINT_COOLDOWN_MS); continue; }
                    const blob = await response.blob();
                    if (!blob.size) continue; // empty body → fail over
                    // A non-image body (e.g. an error page served with 200, which
                    // may carry no Content-Type) → fail over. On the LAST endpoint
                    // accept a typeless body rather than showing nothing, in case a
                    // backend ever returns a valid image without a Content-Type.
                    if (!blob.type.startsWith('image/') && !isLast) continue;
                    endpointFailedUntil.delete(url); // healthy again
                    return URL.createObjectURL(blob);
                } catch (e) {
                    // Network error (incl. CORS on an error page) → cool it off.
                    endpointFailedUntil.set(url, Date.now() + ENDPOINT_COOLDOWN_MS);
                }
            }
            return null; // every endpoint failed
        },
        requestCharacter: async function () {
            const char = JSON.stringify(this.character);
            localStorage.setItem('character', char);

            // Nothing about the sprite changed (e.g. the same state was committed
            // twice on load) → don't ask the backend again.
            if (char === this._lastBody && this.img_src) return;
            this._lastBody = char;

            // Already rendered this exact look? Reuse it. Turning the character
            // left then right, or undoing an item, becomes instant and costs the
            // render backend nothing.
            const cached = this._renderCache.get(char);
            if (cached) {
                this._renderCache.delete(char); // re-insert to mark as most-recent
                this._renderCache.set(char, cached);
                this.img_src = cached;
                return;
            }

            // Guard against out-of-order results: the character can change again
            // while a (possibly failed-over, and thus slower) request is still in
            // flight, so only the most recent request may update the sprite.
            const seq = (this._renderSeq = (this._renderSeq || 0) + 1);

            // Collapse duplicate in-flight requests for the same look.
            let pending = this._inFlight.get(char);
            if (!pending) {
                pending = this.fetchSprite(char).finally(() => this._inFlight.delete(char));
                this._inFlight.set(char, pending);
            }
            const objectUrl = await pending;

            // Every endpoint failed: keep the previous sprite (no console noise).
            if (!objectUrl) return;

            if (!this._renderCache.has(char)) {
                this._renderCache.set(char, objectUrl);
                // Bound the cache; releasing evicted blobs keeps memory flat over a
                // long dress-up session.
                while (this._renderCache.size > this.RENDER_CACHE_LIMIT) {
                    const oldestKey = this._renderCache.keys().next().value;
                    const oldestUrl = this._renderCache.get(oldestKey);
                    this._renderCache.delete(oldestKey);
                    if (oldestUrl !== this.img_src) URL.revokeObjectURL(oldestUrl);
                }
            }

            // A newer request has since been issued — don't clobber its result.
            if (seq === this._renderSeq) this.img_src = objectUrl;
        },
        
        // Method to get the current character sprite as a data URL
        getCharacterImageDataUrl: function(addWatermark = true) {
            try {
                // Get the image element
                const img = this.$refs.characterImage;
                if (!img || !img.complete || !img.naturalHeight) {
                    console.error('Character image not fully loaded');
                    return null;
                }
                
                // Create a canvas element
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                
                // Add extra height for watermark if needed
                const watermarkHeight = addWatermark ? 20 : 0;
                canvas.height = img.naturalHeight + watermarkHeight;
                
                // Draw the image onto the canvas
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                
                // Add watermark if requested
                if (addWatermark) {
                    // Set watermark style
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
                    ctx.lineWidth = 0.5;
                    ctx.font = '12px Arial';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    
                    // Draw watermark text
                    const watermarkText = 'gerado no meurag.com';
                    const x = canvas.width / 2;
                    const y = img.naturalHeight + 10; // 10px from bottom of original image
                    
                    // Draw text with outline for better visibility
                    ctx.strokeText(watermarkText, x, y);
                    ctx.fillText(watermarkText, x, y);
                }
                
                // Convert the canvas to a data URL
                return canvas.toDataURL('image/png');
            } catch (error) {
                console.error('Error capturing character sprite:', error);
                return null;
            }
        }
    },
    computed: {
        ...mapState({
            character: state => state.character,
        }),
    },
    mounted() {
        // Request character image immediately on component mount
        this.requestCharacter();
    },
    beforeUnmount() {
        // Release every sprite URL we allocated on teardown.
        for (const url of this._renderCache.values()) URL.revokeObjectURL(url);
        this._renderCache.clear();
        if (this.img_src && this.img_src.startsWith('blob:') ) {
            URL.revokeObjectURL(this.img_src);
        }
    },
    watch: {
        '$store.state.character': {
            deep: true,
            handler: 'requestCharacter'
        }
    },
}
</script>

<style>
img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}
</style>